import { Component, OnDestroy, QueryList, ViewChild, ViewChildren } from '@angular/core';
import {
  IsKpiBulkRequest,
  MetricBulkRequestCompany,
  MetricBulkRequestCompanyPeriod,
  MetricBulkRequestCompanyWrapper,
  MetricManageRowActionKpiBulkRequest,
  MetricManageRowKpiBulkRequest,
  MetricSharedLinkNotificationDataDTO,
  PeriodRoleKpiBulkRequest,
  SelectedKpiBulkRequest,
  ShowKpiBulkRequest,
} from "../dto/metric-bulk-request";
import { AuthService } from "../../auth/service/auth.service";
import { OrganizationsService } from "../../organization/components/service/organizations.service";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { filter, switchMap, takeUntil, tap } from "rxjs/operators";
import { KpiBusinessService } from "../service/backend/kpi-business.service";
import { firstValueFrom, Subject } from "rxjs";
import { throwErrorOnEmpty } from "../../../util/error/pipe-errors";
import { SharedComponentsModule } from "../../shared/module/shared-components.module";
import {
  KpiUpdateMetricsVisibilityBulkRequestComponent,
} from "./modal/kpi-update-metrics-visibility-bulk-request/kpi-update-metrics-visibility-bulk-request.component";
import { Clipboard } from "@angular/cdk/clipboard";
import {
  PublicSharedLinkPlanning,
} from "../components/modal-public-link/form/modal-metric-public-shared-link-form.component";
import { LocalDate, LocalMonthDate } from "../../../core/pojo/date-variable";
import { MetricSharedLinkService } from "../service/backend/metric-shared-link-service";
import { NOTIFICATIONS, NotificationsService } from "../../../core/service/notifications.service";
import { MetricSharedLinkContact, MetricSharedLinkWrapper } from "../dto/MetricSharedLinkWrapper";
import { DateService } from "../../../util/common/date-service/date.service";
import { MetricPeriod, MetricPeriodWrapper } from "../dto/MetricGroupsAndPeriods";
import { UUID } from "../../../core/pojo/id-variable";
import { ModalManagePeriodsComponent } from "../modals/modal-manage-periods/modal-manage-periods.component";
import { getKpiCompanyMetricsUrl } from "../module/kpi-module-routing.module";
import { ActivatedRoute, Router } from "@angular/router";
import { PortfolioAnalysisService } from "../service/backend/portfolio-analysis.service";
import { document } from "ngx-bootstrap/utils";
import { skeletonAnimations } from "../../../util/animations/skeleton-animations";
import { KpiModalBulkMslComponent } from "../modals/kpi-modal-bulk-msl/kpi-modal-bulk-msl.component";
import { KpiModalDeletePeriodsComponent } from "../modals/kpi-modal-delete-periods/kpi-modal-delete-periods.component";
import {
  KpiModalBulkKpiRequestComponent,
} from "../modals/kpi-modal-bulk-kpi-request/kpi-modal-bulk-kpi-request.component";
import { SwitcherComponent } from "../../shared/component/switcher/switcher.component";
import { FormControl } from "@angular/forms";
import { ModalButtons } from "../../shared/component/modal-container/modal-container.component";
import {
  KpiModalMslUpdateContactComponent,
} from "../modals/kpi-modal-msl-update-contact/kpi-modal-msl-update-contact.component";
import {
  TableGroupIconComponent,
} from "../../shared/component/icons-deprecated/table-group-icon/table-group-icon.component";
import { GroupingKpiBulkRequest } from "../service/internal/track-untrack-grouping-helper";

@Component({
  selector: 'app-kpi-bulk-request',
  standalone: true,
  imports: [
    SharedComponentsModule,
    KpiUpdateMetricsVisibilityBulkRequestComponent,
    ModalManagePeriodsComponent,
    KpiModalBulkMslComponent,
    KpiModalDeletePeriodsComponent,
    KpiModalBulkKpiRequestComponent,
    KpiModalMslUpdateContactComponent,
    TableGroupIconComponent,
  ],
  templateUrl: './kpi-bulk-request.component.html',
  styleUrl: './kpi-bulk-request.component.scss',
  animations: [skeletonAnimations],

})
export class KpiBulkRequestComponent implements OnDestroy {
  protected readonly DateService = DateService;

  private _destroyStream: Subject<void> = new Subject<void>();
  private isMslToggleInAction = false;
  private fetchedPeriods: LocalDate[];

  readonly resetText = 'Reset';
  readonly PeriodRoles: PeriodRoleKpiBulkRequest[] = ['actual', 'budget'];
  readonly Grouping: GroupingKpiBulkRequest[] = ['tracked', 'untracked'];
  readonly MAX_NAME_LENGTH: number = 25;

  companyWrappers: MetricBulkRequestCompanyWrapper[];
  uniquePeriods: LocalDate[];
  is = new IsKpiBulkRequest();
  show = new ShowKpiBulkRequest();
  selected = new SelectedKpiBulkRequest();

  buttons: MetricManageRowKpiBulkRequest[] = [
    {
      iconAndStyling: 'plus.svg',
      name: 'Add periods',
      action: "add periods",
    },
    {
      iconAndStyling: 'delete-outline.svg;',
      name: 'Delete periods',
      nameStyling: 'red',
      action: 'delete periods',
    },
  ];

  allMSLToggleForm = new FormControl<boolean>(false);
  deletionInProcess = false;
  currentIndexForTitleProgress = 0;
  periodsByYears: DatesByYears[];

  @ViewChildren('mslSingleSwitcher') singleMSLSwitchers: QueryList<SwitcherComponent>;
  @ViewChild('bulkMSLToggle') allLinksToggle: SwitcherComponent;


  constructor(private authService: AuthService,
    private orgService: OrganizationsService,
    private kpiService: KpiBusinessService,
    private clipboard: Clipboard,
    private sharedLinkService: MetricSharedLinkService,
    private notificationService: NotificationsService,
    private route: ActivatedRoute,
    private router: Router,
    private portfolioAnalysisService: PortfolioAnalysisService
  ) {
    this.initSubscriptions();
  }

  ngOnDestroy(): void {
    this._destroyStream.next();
    this._destroyStream.complete();
  }

  async closeKpiRequestModal(isSuccessful: boolean): Promise<void> {
    this.show.kpiRequestModal = false;
    if (isSuccessful) {
      await this.getCompanyWrappers();
    }
  }

  setPeriodModal(type: MetricManageRowActionKpiBulkRequest): void {
    switch (type) {
      case "add periods":
        this.show.managePeriodsModal = true;
        break;
      case "delete periods":
        this.show.deletePeriodsModal = true;
        break;
    }
  }

  /**
   * Prepares the data for company metrics settings and then shows the modal.
   *
   * For the modal we need companies, from which we will be able to copy the metrics visibility.
   * We want to provide only those companies for the selection, which have at least one visible metric.
   * @param company for which the modal will be shown.
   */
  async showCompanyMetricSettings(company: MetricBulkRequestCompanyWrapper): Promise<void> {
    this.selected.companyWrapper = company;
    this.selected.companiesForManageMetricsModal = this.companyWrappers;
    this.selected.companyMetricGroups =
      await firstValueFrom(this.kpiService.getMetricGroupsForCompany(this.selected.org.uuid, company.company.uuid));
    this.show.updateMetricsModal = true;
  }

  async closeModalBulkMsl(isSuccessful: boolean): Promise<void> {
    this.show.bulkMslModal = false;
    if (isSuccessful) {
      await this.getCompanyWrappers();
    }
  }

  async updateCompanyMetric(isSuccessful: boolean): Promise<void> {
    this.show.updateMetricsModal = false;
    if (isSuccessful) {
      await this.getCompanyWrappers();
    }
  }

  buildCredentials(form: MetricBulkRequestCompanyWrapper): string {
    const content = form.linkForm.getFormRawValue();
    return `Company: ${form.company.name}\nLink: ${content.token}\nPassword: ${content.password}`;
  }

  copyToClipboard(str: string): void {
    this.clipboard.copy(str);
    this.notificationService.success(NOTIFICATIONS.COPIED_NO_TITLE);
  }

  showUpdateContact(form: MetricBulkRequestCompanyWrapper): void {
    this.selected.mslFormForContactUpdate = form;
  }

  async closeUpdateContact(isSuccessful: boolean): Promise<void> {
    if (isSuccessful) {
      await this.updateMsl(this.selected.mslFormForContactUpdate);
    }
    this.selected.mslFormForContactUpdate = null;
  }

  openLink(link: string): void {
    if (!link) {
      return;
    }
    window.open(link);
  }

  async toggleMsl(wrapper: MetricBulkRequestCompanyWrapper): Promise<void> {
    if (this.isMslToggleInAction) {
      return;
    }
    try {
      this.isMslToggleInAction = true;
      if (wrapper.linkForm.form.value.uuid) {
        this.selected.companyWrapper = wrapper;
        this.show.deleteOneMSLModal = true;
      } else {
        await this.createMsl(wrapper, false);
      }
    } finally {
      this.isMslToggleInAction = false;
      this.allMSLToggleForm.patchValue(this.allMSLCreated());
    }
  }

  /**
   * Selects the provided period and by condition updates a msl.
   *
   * If we receive 'Reset', then both from and till will be set to null. MSL is updated.
   * If both from and till are provided, then MSL is also updated.
   *
   * @param company for which we are working and praying and leleim.
   * @param planning is either actual or budget.
   * @param fromPeriod cannot be later than tillPeriod.
   * @param tillPeriod cannot be earlier than fromPeriod.
   */
  async updatePeriodSelector(company: MetricBulkRequestCompanyWrapper,
    planning: PublicSharedLinkPlanning,
    fromPeriod: LocalMonthDate,
    tillPeriod: LocalMonthDate): Promise<void> {
    if (fromPeriod === this.resetText || tillPeriod === this.resetText) {
      if (planning === 'actual') {
        company.linkForm.form.controls.actual.controls.fromPeriod.patchValue(null);
        company.linkForm.form.controls.actual.controls.tillPeriod.patchValue(null);
      } else {
        company.linkForm.form.controls.budget.controls.fromPeriod.patchValue(null);
        company.linkForm.form.controls.budget.controls.tillPeriod.patchValue(null);
      }
      await this.updateMsl(company);
      return;
    }
    company.linkForm.updatePeriodSelector(planning, fromPeriod, tillPeriod);
    if (Boolean(fromPeriod) && Boolean(tillPeriod)) {
      await this.updateMsl(company);
    }
  }

  async closeDeletePeriodsModal(isSuccessful: boolean): Promise<void> {
    this.show.deletePeriodsModal = false;
    if (isSuccessful) {
      await this.getCompanyWrappers();
    }
  }

  async closeModalManagePeriods(isSuccessful: boolean): Promise<void> {
    this.show.managePeriodsModal = false;
    if (isSuccessful) {
      await this.getCompanyWrappers();
    }
  }

  async goToCompanyMetrics(companyUUID: UUID): Promise<void> {
    await this.router.navigate(
      [getKpiCompanyMetricsUrl(companyUUID)],
      { queryParams: this.route.snapshot.queryParams });
  }

  async updateTrackStatus(company: MetricBulkRequestCompany, isTracked: boolean): Promise<void> {
    await firstValueFrom(this.portfolioAnalysisService.updateIsTrackedStatus(company.uuid, isTracked)
      .pipe(
        takeUntil(this._destroyStream),
        throwErrorOnEmpty(undefined, 'Could not update track status in kpi metrics')
      ));

    company.isTracked = isTracked;
    this.updateGroupingAmount();
  }

  showTooltipCompanyWrapper(id: string, entry: MetricBulkRequestCompanyPeriod): void {
    if (!entry || !entry.isPersisted) {
      this.show.tooltip = null;
      return;
    }
    this.show.tooltip = {
      tooltipCoordinates: document.getElementById(id).getBoundingClientRect(),
      companyWrapper: entry,
    };
  }

  showTooltipPOG(id: string, entry: MetricSharedLinkContact): void {
    if (!entry) {
      this.show.pocTooltip = null;
      return;
    }
    const tooltipCoordinates = document.getElementById(id).getBoundingClientRect();
    if (!entry.email && !entry.name) {
      this.show.pocTooltip = {
        tooltipCoordinates,
        tooltipTitle: 'Add point of contact',
        tooltipText: '',
      };
    } else {
      this.show.pocTooltip = {
        tooltipCoordinates,
        tooltipTitle: entry.name,
        tooltipText: entry.email,
      };
    }
  }

  allMSLCreated(): boolean {
    // as side effect it shows as enabled if no companies are tracked
    return this.companyWrappers
      .filter((c) => c.company.isTracked)
      .every((c) => c.linkForm.form.value.uuid);
  }

  // if NOT every link created - then create rest, else - delete the others
  toggleMSLForAllCompanies(): void {
    if (this.allMSLCreated()) {
      this.show.deleteAllMSLModal = true;
    } else {
      this.createAllMSL();
    }
  }

  showEnableAllMSLTooltip(id: string): void {
    this.show.allMslTooltip = {
      tooltipCoordinates: document.getElementById(id).getBoundingClientRect(),
      tooltipText: this.allMSLToggleForm.value ? `Delete all links` : 'Create all links',
    };
  }

  async deleteOneMSLConfirmed(shouldDelete: boolean): Promise<void> {
    if (!shouldDelete) {
      // re-enable switcher
      const switcher = this.singleMSLSwitchers
        .filter((v) => v.id === 'kpi-bulk-request-table-td-toggle-msl-' + this.selected.companyWrapper.company.uuid)
        .at(0);
      switcher.writeValue(true);
    } else {
      await this.deleteMsl(this.selected.companyWrapper, true);
    }
    this.selected.companyWrapper = undefined;
    this.allMSLToggleForm.patchValue(this.allMSLCreated());
    this.show.deleteOneMSLModal = false;
  }

  async deleteAllMSL(shouldDelete: boolean): Promise<void> {
    if (!shouldDelete) {
      this.show.deleteAllMSLModal = false;
      this.allMSLToggleForm.patchValue(this.allMSLCreated());
      return;
    }
    this.deletionInProcess = true;
    for (let i = 0; i < this.companyWrappers.length; i++) {
      const c = this.companyWrappers[i];
      if (c.company.isTracked && c.linkForm.form.value.uuid) {
        this.currentIndexForTitleProgress = i;
        await this.deleteMsl(c, false);
      }
    }
    this.currentIndexForTitleProgress = 0;
    this.deletionInProcess = false;
    this.show.deleteAllMSLModal = false;
  }

  mslDeletionModalTitle(): string {
    return this.deletionInProcess
      ? `Deleting links, ${Math.round(this.currentIndexForTitleProgress / this.companyWrappers.length * 100)}% done`
      : 'Delete all shared links';
  }

  mslDeletionModalButtons(): ModalButtons {
    return this.deletionInProcess
      ? {} // no buttons
      : null; // use default
  }

  mslDeletionModalMainText(): string[] {
    const trackedAmount = this.companyWrappers
      .filter((c) => c.company.isTracked)
      .length;
    return [`${trackedAmount} links & passwords will be deleted`, `Everyone will lose access to KPI data`];
  }

  singleMslDeletionModalMainText(): string[] {
    return [`This link & password will be deleted`, `Everyone will lose access to KPI data`];
  }

  mslCreationModalTitle(): string {
    return `Creating links, ${Math.round(this.currentIndexForTitleProgress / this.companyWrappers.length * 100)}% done`;
  }

  showRequestRecipientsTooltip(id: string, dto: MetricSharedLinkNotificationDataDTO): void {
    this.show.requestRecipientsTooltip = {
      tooltipText: dto.recipientsNames.join("\n"),
      tooltipCoordinates: document.getElementById(id).getBoundingClientRect(),
      titleText: dto.recipientsNames.length > 1 ? dto.recipientsNames.length + ' recipients' : '1 recipient',
    };
  }

  showRequestSentTooltip(id: string, dto: MetricSharedLinkNotificationDataDTO): void {
    this.show.requestSentTooltip = {
      tooltipText: DateService.formatLocalDateTime(dto.creationTime),
      tooltipCoordinates: document.getElementById(id).getBoundingClientRect(),
    };
  }

  showRequestReceivedTooltip(id: string, dto: MetricSharedLinkNotificationDataDTO): void {
    this.show.requestReceivedTooltip = {
      tooltipText: DateService.formatLocalDateTime(dto.submissionTime),
      tooltipCoordinates: document.getElementById(id).getBoundingClientRect(),
    };
  }

  showCompanyNameTooltip(id: string, name: string): void {
    this.show.companyNameTooltip = {
      tooltipText: name,
      tooltipCoordinates: document.getElementById(id).getBoundingClientRect(),
    };
  }

  showCopyCredentialsTooltip(id: string): void {
    this.show.copyCredentialsTooltip = {
      tooltipText: 'Copy credentials',
      tooltipCoordinates: document.getElementById(id).getBoundingClientRect(),
    };
  }

  showCopyPasswordTooltip(id: string): void {
    this.show.copyPasswordTooltip = {
      tooltipText: 'Copy password',
      tooltipCoordinates: document.getElementById(id).getBoundingClientRect(),
    };
  }

  showCopyLinkTooltip(id: string): void {
    this.show.copyLinkTooltip = {
      tooltipText: 'Copy link',
      tooltipCoordinates: document.getElementById(id).getBoundingClientRect(),
    };
  }

  findFirstWithYear(year: number, periods: MetricBulkRequestCompanyPeriod[]): number {
    return periods.findIndex((p) => new Date(Date.parse(p.period)).getFullYear() === year);
  }

  calculateVariableColumnsAmount(): number {
    // we add this.periodsByYears.length - 1 in the end to take into account all the gaps between years
    return this.periodsByYears
      .reduce((acc, next) => (!next.isShown ? acc + 1 : acc + next.periods.length), 0) +
      this.periodsByYears.length - 1;
  }

  unloadYear(periodWrapper: DatesByYears): void {
    periodWrapper.isShown = false;
  }

  async loadYear(yearWrapper: DatesByYears): Promise<void> {
    if (yearWrapper.periods.some((p) => this.fetchedPeriods.includes(p))) {
      yearWrapper.isShown = true;
      return;
    }

    const newPeriods = await firstValueFrom(this.kpiService.fetchPeriods(this.selected.org.uuid, yearWrapper.periods));
    this.fetchedPeriods = this.fetchedPeriods.concat(yearWrapper.periods).sort();

    newPeriods.forEach((response) => {
      const wrapper = this.companyWrappers.find((wr) => wr.company.uuid === response.companyUuid);
      wrapper.periods.actual = wrapper.periods.actual.concat(response.periods.actual).sort((l, r) => l.period.localeCompare(r.period));
      wrapper.periods.budget = wrapper.periods.budget.concat(response.periods.budget).sort((l, r) => l.period.localeCompare(r.period));
    });

    this.companyWrappers.forEach((wrapper) => {
      MetricBulkRequestCompanyWrapper.deserialize(wrapper, this.fetchedPeriods, this.uniquePeriods);
    });

    yearWrapper.isShown = true;
  }

  private initPeriodsByYears(): void {
    if (!this.uniquePeriods) {
      return;
    }
    const periods: DatesByYears[] = [];
    this.uniquePeriods.forEach((period) => {
      const date = new Date(Date.parse(period));
      if (periods.length === 0 || periods[periods.length - 1].year !== date.getFullYear()) {
        periods.push({
          year: date.getFullYear(),
          periods: [period],
          isShown: false,
        });
      } else {
        periods[periods.length - 1].periods.push(period);
      }
    });

    this.periodsByYears = periods;
  }

  private markRequestedPeriods(company: MetricBulkRequestCompanyWrapper): void {
    const fromActual = new Date(company.linkForm.getFormRawValue().actual.fromPeriod);
    const tillActual = new Date(company.linkForm.getFormRawValue().actual.tillPeriod);
    company.periods.actual.forEach((p) => {
      const date = new Date(p.period);
      p.isRequested = date <= tillActual && date >= fromActual;
    });
    const fromBudget = new Date(company.linkForm.getFormRawValue().budget.fromPeriod);
    const tillBudget = new Date(company.linkForm.getFormRawValue().budget.tillPeriod);
    company.periods.budget.forEach((p) => {
      const date = new Date(p.period);
      p.isRequested = date <= tillBudget && date >= fromBudget;
    });
  }

  private async createAllMSL(): Promise<void> {
    this.show.createAllMSLModal = true;

    for (let i = 0; i < this.companyWrappers.length; i++) {
      const c = this.companyWrappers[i];
      this.currentIndexForTitleProgress = i;
      if (c.company.isTracked && !c.linkForm.form.value.uuid) {
        await this.createMsl(c, false);
      }
    }

    this.currentIndexForTitleProgress = 0;
    this.show.createAllMSLModal = false;
  }

  private async deleteMsl(wrapper: MetricBulkRequestCompanyWrapper, showNotification: boolean): Promise<void> {
    await firstValueFrom(this.sharedLinkService.delete(wrapper.linkForm.form.value.uuid));
    this.initValuesAfterCrud(wrapper, null);
    if (showNotification) {
      this.notificationService.success({ title: 'Success', message: 'Shared link was successfully deleted.' });
    }
    this.markRequestedPeriods(wrapper);
  }

  private async createMsl(wrapper: MetricBulkRequestCompanyWrapper, showNotification: boolean): Promise<void> {
    const form = wrapper.linkForm.getFormRawValue();
    this.mslPostValidation(form);
    const msl = await firstValueFrom(this.sharedLinkService.create(form));
    this.initValuesAfterCrud(wrapper, msl);
    if (showNotification) {
      this.notificationService.success({ title: 'Success', message: 'Shared link was successfully created.' });
    }
    this.markRequestedPeriods(wrapper);
  }

  /**
   * Prepares the msl and then updates it.
   *
   * If from or till period is not provided, then both will be discarded from saving.
   * (In UI the values are not reset till manual refresh)
   *
   * @param company for which the msl will be updated.
   */
  private async updateMsl(company: MetricBulkRequestCompanyWrapper): Promise<void> {
    if (!company.linkForm.form.value.uuid) {
      return;
    }
    const form = company.linkForm.getFormRawValue();
    if (!Boolean(form.actual.tillPeriod) || !Boolean(form.actual.fromPeriod)) {
      form.actual.fromPeriod = null;
      form.actual.tillPeriod = null;
    }
    if (!Boolean(form.budget.tillPeriod) || !Boolean(form.budget.fromPeriod)) {
      form.budget.fromPeriod = null;
      form.budget.tillPeriod = null;
    }
    this.mslPostValidation(form);
    await firstValueFrom(this.sharedLinkService.update(form));
    this.notificationService.success({ title: 'Success', message: 'Shared link was successfully updated.' });
    this.markRequestedPeriods(company);
  }

  private initValuesAfterCrud(wrapper: MetricBulkRequestCompanyWrapper, msl?: MetricSharedLinkWrapper): void {
    wrapper.linkForm.onDestroy();
    wrapper.linkForm.initializeValues(wrapper.company.uuid,
      { actual: wrapper.linkForm.actualPeriods, budget: wrapper.linkForm.budgetPeriods },
      msl,
      null);
    wrapper.checkAfterInitValues();
  }

  private mslPostValidation(wrapper: MetricSharedLinkWrapper): void {
    wrapper.actual.isActivated = Boolean(wrapper.actual.fromPeriod) && Boolean(wrapper.actual.tillPeriod);
    wrapper.budget.isActivated = Boolean(wrapper.budget.fromPeriod) && Boolean(wrapper.budget.tillPeriod);
  }

  private initSubscriptions(): void {
    this.authService.currentUserTypeForServices$.pipe(takeUntilDestroyed())
      .subscribe((userType) => {
        if (!userType) {
          return;
        }
        this.is.admin = userType === 'admin';
      });

    this.orgService.selected$
      .pipe(
        filter((org) => Boolean(org)),
        tap((org) => this.selected.org = org),
        switchMap(() => this.getCompanyWrappers()),
        takeUntilDestroyed())
      .subscribe();
  }

  private async getCompanyWrappers(): Promise<void> {
    this.resetData();
    this.uniquePeriods = await firstValueFrom(this.kpiService.getUniquePeriods(this.selected.org.uuid));
    this.initPeriodsByYears();
    this.initFetchedPeriods();

    this.companyWrappers = await firstValueFrom(this.kpiService.getBulkRequestCompanyWrappers(this.selected.org.uuid, this.fetchedPeriods, this.uniquePeriods)
      .pipe(
        takeUntil(this._destroyStream),
        throwErrorOnEmpty(undefined as MetricBulkRequestCompanyWrapper[], 'Could not fetch company wrappers')
      ));

    this.allMSLToggleForm.patchValue(this.allMSLCreated());
    this.updateGroupingAmount();
    this.setNearestPeriodToScrollOnInit();
    this.setPeriodsForManagePeriodsModal();
    this.setPeriodsForDeletePeriodsModal();
    this.setCompaniesForManagePeriodsModal();
  }

  private initFetchedPeriods(): void {
    const now = new Date();
    const years = [now.getFullYear() - 1, now.getFullYear(), now.getFullYear() + 1];
    let yearsToFetch = this.periodsByYears.filter((p) => years.includes(p.year));
    if (!yearsToFetch.length) {
      yearsToFetch = this.periodsByYears.slice(-3);
    }

    yearsToFetch.forEach((year) => {
      year.isShown = true;
    });

    this.fetchedPeriods = yearsToFetch.flatMap((p) => p.periods);
  }

  private updateGroupingAmount(): void {
    this.show.grouping.tracked.amount = this.companyWrappers.filter((wrapper) => wrapper.company.isTracked).length;
    this.show.grouping.untracked.amount = this.companyWrappers.filter((wrapper) => !wrapper.company.isTracked).length;
  }

  private setCompaniesForManagePeriodsModal(): void {
    if (!this.companyWrappers.length) {
      return;
    }
    this.selected.companiesForManagePeriodsModal = this.companyWrappers
      .map((companyWrapper) => companyWrapper.company);
  }

  /**
   * On init, we want to scroll the periods to the +- current day.
   * We choose the periods, which are starting from today. If such periods exist, we choose the first one.
   * Otherwise, we take the oldest period.
   *
   * P.S. actual and budget have the same periods, therefore I take actual.
   */
  private setNearestPeriodToScrollOnInit(): void {
    if (!this.companyWrappers.length || !this.companyWrappers[0].periods.actual.length) {
      return;
    }
    const periods = this.companyWrappers[0].periods.actual.map((wrapper) => wrapper.period);
    const today = DateService.toHyphenDate(new Date());
    const periodsAfterToday = periods.filter((period) => period >= today);
    this.selected.periodToScrollOnInit = periodsAfterToday.length ? periodsAfterToday[0] : periods.at(-1);
  }

  /**
   * Provides periods for the manage modal:
   * A period is provided if it is persisted in all companies.
   *
   * P.S. actual and budget have the same periods, therefore I take actual for `const periods`.
   */
  private setPeriodsForManagePeriodsModal(): void {
    if (!this.companyWrappers.length || !this.companyWrappers[0].periods.actual.length) {
      return;
    }
    const periodsForManagePeriodsModal: MetricPeriodWrapper = {
      actual: [],
      budget: [],
    };

    // Helper function to get persisted periods
    const getPersistedPeriods = (periods: MetricBulkRequestCompanyPeriod[]): MetricPeriod[] => {
      const metricDataMap = new Map<string, MetricBulkRequestCompanyPeriod[]>();
      periods.forEach((metricData) => {
        if (!metricDataMap.has(metricData.period)) {
          metricDataMap.set(metricData.period, []);
        }
        metricDataMap.get(metricData.period).push(metricData);
      });

      return Array.from(metricDataMap.entries())
        .filter(([, metricData]) => metricData.every((wrapper) => wrapper.isPersisted))
        .map(([period]) => ({ period, isRequested: false })); // isRequested doesn't matter
    };

    const actualPeriods = this.companyWrappers.flatMap((companyWrapper) => companyWrapper.periods.actual);
    const budgetPeriods = this.companyWrappers.flatMap((companyWrapper) => companyWrapper.periods.budget);

    periodsForManagePeriodsModal.actual = getPersistedPeriods(actualPeriods);
    periodsForManagePeriodsModal.budget = getPersistedPeriods(budgetPeriods);

    this.selected.periodsForManagePeriodsModal = periodsForManagePeriodsModal;
  }

  private setPeriodsForDeletePeriodsModal(): void {
    if (!this.companyWrappers.length || !this.uniquePeriods) {
      return;
    }
    const periods: MetricPeriod[] = this.uniquePeriods
      .map((period) => ({ period: period, isRequested: false }));
    this.selected.periodsForDeletePeriodsModal = {
      actual: periods,
      budget: periods,
    };
  }

  private resetData(): void {
    const selectedOrg = this.selected.org;
    this.selected = new SelectedKpiBulkRequest();
    this.selected.org = selectedOrg; // do not reset org :)
    this.show = new ShowKpiBulkRequest();
    this.companyWrappers = undefined;
  }
}

class DatesByYears {
  year: number;
  periods: LocalDate[];
  isShown: boolean;
}
